import { useState, useEffect, useCallback } from 'react';
import { UserPlus, Trash2, KeyRound, RefreshCw, ShieldCheck, User } from 'lucide-react';
import { usersApi, extractErrorMessage, type UserRecord } from '../services/api';

type FormMode = 'create' | 'password' | null;

export default function UsersTab() {
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // form state
  const [formMode, setFormMode] = useState<FormMode>(null);
  const [targetUser, setTargetUser] = useState<string>('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'user'>('user');
  const [submitting, setSubmitting] = useState(false);

  const notify = (msg: string, isError = false) => {
    if (isError) { setError(msg); setSuccess(null); }
    else { setSuccess(msg); setError(null); }
    setTimeout(() => { setError(null); setSuccess(null); }, 4000);
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await usersApi.list();
      setUsers(res.data.users);
    } catch (e: any) {
      notify(extractErrorMessage(e, 'Failed to load users'), true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreate = () => {
    setFormMode('create');
    setTargetUser('');
    setUsername('');
    setPassword('');
    setRole('user');
  };

  const openChangePassword = (u: UserRecord) => {
    setFormMode('password');
    setTargetUser(u.username);
    setPassword('');
  };

  const closeForm = () => { setFormMode(null); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (formMode === 'create') {
        await usersApi.create(username.trim(), password, role);
        notify(`User "${username.trim()}" created`);
      } else if (formMode === 'password') {
        await usersApi.changePassword(targetUser, password);
        notify(`Password updated for "${targetUser}"`);
      }
      closeForm();
      fetchUsers();
    } catch (e: any) {
      notify(extractErrorMessage(e, 'Operation failed'), true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (u: UserRecord) => {
    if (!confirm(`Delete user "${u.username}"?`)) return;
    try {
      await usersApi.delete(u.username);
      notify(`User "${u.username}" deleted`);
      fetchUsers();
    } catch (e: any) {
      notify(extractErrorMessage(e, 'Delete failed'), true);
    }
  };

  return (
    <div className="flex h-full flex-col gap-6 p-6 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white">User Management</h2>
          <p className="text-sm text-white/50">Create, delete, and manage studio user credentials</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchUsers}
            disabled={loading}
            className="studio-icon-button"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 rounded-lg bg-[var(--studio-violet)] px-3 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity"
          >
            <UserPlus className="h-4 w-4" />
            New User
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-lg border border-green-500/40 bg-green-500/10 px-4 py-3 text-sm text-green-400">
          {success}
        </div>
      )}

      {/* Create / change-password form */}
      {formMode && (
        <div className="rounded-xl border border-white/10 bg-white/5 p-5">
          <h3 className="mb-4 text-sm font-semibold text-white/80">
            {formMode === 'create' ? 'Create New User' : `Change Password — ${targetUser}`}
          </h3>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {formMode === 'create' && (
              <>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-white/50">Username</label>
                  <input
                    type="text"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    required
                    autoFocus
                    placeholder="e.g. john"
                    className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-[var(--studio-violet)] transition-colors"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-white/50">Role</label>
                  <select
                    value={role}
                    onChange={e => setRole(e.target.value as 'admin' | 'user')}
                    className="rounded-lg border border-white/10 bg-[#1a1a2e] px-3 py-2 text-sm text-white outline-none focus:border-[var(--studio-violet)] transition-colors"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </>
            )}
            <div className="flex flex-col gap-1">
              <label className="text-xs text-white/50">Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={6}
                placeholder="Min 6 characters"
                className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-[var(--studio-violet)] transition-colors"
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-2 rounded-lg bg-[var(--studio-violet)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                {submitting ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : null}
                {formMode === 'create' ? 'Create' : 'Update Password'}
              </button>
              <button
                type="button"
                onClick={closeForm}
                className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/60 hover:text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users table */}
      <div className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
        {loading && users.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-white/30 text-sm">
            Loading users…
          </div>
        ) : users.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-white/30 text-sm">
            No users found
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left text-xs text-white/40 uppercase tracking-wider">
                <th className="px-4 py-3">Username</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.username} className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
                  <td className="px-4 py-3 font-medium text-white">{u.username}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      u.role === 'admin'
                        ? 'bg-[var(--studio-violet)]/20 text-[var(--studio-violet)]'
                        : 'bg-white/10 text-white/60'
                    }`}>
                      {u.role === 'admin' ? <ShieldCheck className="h-3 w-3" /> : <User className="h-3 w-3" />}
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-white/40">
                    {new Date(u.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openChangePassword(u)}
                        className="rounded-md p-1.5 text-white/40 hover:text-white hover:bg-white/10 transition-colors"
                        title="Change password"
                      >
                        <KeyRound className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(u)}
                        className="rounded-md p-1.5 text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        title="Delete user"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-xs text-white/25">
        Only admin users can access this panel. Passwords are stored as salted SHA-256 hashes.
      </p>
    </div>
  );
}
